import { Link, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { type PropsWithChildren } from 'react';
import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  type TextProps,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { SITE_NAME, SUPPORT_EMAIL } from '@/constants/site';
import { MAX_WIDTH, colors, radius, type } from '@/theme';

/**
 * The three standalone pages the app stores link to — privacy, terms, support.
 * They share this shell so the chrome (back control, heading block, cross-links)
 * stays identical and each page file is nothing but its own copy.
 *
 * Everything scrolls inside a ScrollView on purpose: `+html.tsx` renders
 * `<ScrollViewStyleReset />`, which pins html/body at 100% and hands scrolling
 * to React Native's ScrollView. Long text in a plain View clips at the fold on
 * web while looking fine on native.
 */

const NAV: { href: '/privacy' | '/terms' | '/support'; label: string }[] = [
  { href: '/privacy', label: 'Privacy' },
  { href: '/terms', label: 'Terms' },
  { href: '/support', label: 'Support' },
];

/**
 * Per-route title and description. `_layout.tsx` emits the site-wide versions
 * from its own `<Head>`; react-helmet dedupes by tag identity with the innermost
 * mount winning, so these — rendered inside the layout — are what reaches the
 * document. Every tag overridden here must have its default in helmet too, never
 * in the `+html.tsx` shell, which helmet can't see or replace.
 *
 * Web only, matching `DocumentTitle`: on native the same component would
 * register an NSUserActivity for Handoff, which these pages have no use for.
 */
function PageHead({ title, description }: { title: string; description: string }) {
  if (Platform.OS !== 'web') return null;
  const full = `${title} — ${SITE_NAME}`;
  return (
    <Head>
      <title>{full}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={full} />
      <meta property="og:description" content={description} />
      <meta name="twitter:title" content={full} />
      <meta name="twitter:description" content={description} />
    </Head>
  );
}

type LegalPageProps = PropsWithChildren<{
  /** Small caps label above the title, e.g. `LEGAL`. */
  kicker: string;
  title: string;
  /** One line under the title — the date stamp, or a lede. */
  subtitle?: string;
  /** Feeds `<title>` and the meta description on web. */
  description: string;
  /** Which of the three cross-links to render as plain text instead of a link. */
  current: (typeof NAV)[number]['href'];
}>;

export function LegalPage({
  kicker,
  title,
  subtitle,
  description,
  current,
  children,
}: LegalPageProps) {
  const router = useRouter();

  // Deep links and store links land here with an empty history stack, so
  // `back()` would be a no-op — replace with the title screen instead.
  const onBack = () => (router.canGoBack() ? router.back() : router.replace('/'));

  return (
    <SafeAreaView style={styles.safe}>
      <PageHead title={title} description={description} />
      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag">
        <View style={styles.container}>
          <Pressable
            onPress={onBack}
            style={({ pressed }) => [styles.back, pressed && styles.pressed]}>
            <Text style={styles.backLabel}>← {SITE_NAME}</Text>
          </Pressable>

          <View style={styles.head}>
            <Text style={styles.kicker}>{kicker}</Text>
            <Text style={styles.title}>{title}</Text>
            {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
          </View>

          <View style={styles.body}>{children}</View>

          <View style={styles.footer}>
            <View style={styles.footerNav}>
              {NAV.map(({ href, label }) =>
                href === current ? (
                  <Text key={href} style={styles.footerCurrent}>
                    {label}
                  </Text>
                ) : (
                  <Link key={href} href={href} style={styles.footerLink}>
                    {label}
                  </Link>
                ),
              )}
            </View>
            <Text style={styles.footerNote}>{SITE_NAME} — a game about drafting a company.</Text>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

/** A titled block of copy. */
export function Section({ title, children }: PropsWithChildren<{ title: string }>) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

/** A paragraph. Nest `<Strong>` or `<Mail />` inside for inline emphasis. */
export function P({ children }: PropsWithChildren) {
  return <Text style={styles.p}>{children}</Text>;
}

export function Strong({ children }: PropsWithChildren) {
  return <Text style={styles.strong}>{children}</Text>;
}

/** A dashed list item. Plain Views keep the marker from wrapping under the text. */
export function Bullet({ children }: PropsWithChildren) {
  return (
    <View style={styles.bullet}>
      <Text style={styles.bulletMark}>—</Text>
      <Text style={styles.p}>{children}</Text>
    </View>
  );
}

/** Boxed aside for the one thing on a page a reader must not miss. */
export function Callout({ children }: PropsWithChildren) {
  return (
    <View style={styles.callout}>
      <Text style={styles.calloutText}>{children}</Text>
    </View>
  );
}

/**
 * The support address as a `mailto:`.
 *
 * Two renderings on purpose. react-native-web turns `<Text href>` into a real
 * `<a>`, which puts the address in the statically exported HTML — copyable,
 * right-clickable, and working before hydration, which matters when the page's
 * job is to be a contact channel a store reviewer can use. React Native's types
 * don't model `href` since native has no anchor; there, `Linking` opens the mail
 * app instead. Only one of the two is wired up at a time, so a web click can't
 * both follow the link and fire `openURL`.
 */
export function Mail({ subject }: { subject?: string }) {
  const href = subject
    ? `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`
    : `mailto:${SUPPORT_EMAIL}`;

  if (Platform.OS === 'web') {
    const anchor = { href } as unknown as TextProps;
    return (
      <Text {...anchor} style={styles.mail}>
        {SUPPORT_EMAIL}
      </Text>
    );
  }

  return (
    <Text style={styles.mail} onPress={() => Linking.openURL(href).catch(() => {})}>
      {SUPPORT_EMAIL}
    </Text>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  scroll: {
    flexGrow: 1,
  },
  container: {
    width: '100%',
    maxWidth: MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 48,
    gap: 26,
  },
  back: {
    alignSelf: 'flex-start',
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.borderSoft,
    backgroundColor: colors.card,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  backLabel: {
    ...type.label,
    fontSize: 10,
    color: colors.dim,
  },
  pressed: {
    opacity: 0.65,
  },
  head: {
    gap: 8,
  },
  kicker: {
    ...type.label,
    fontSize: 10,
    color: colors.gold,
  },
  title: {
    ...type.display,
    fontSize: 34,
    lineHeight: 38,
    color: colors.text,
  },
  subtitle: {
    ...type.body,
    fontSize: 13,
    color: colors.faint,
  },
  body: {
    gap: 24,
  },
  section: {
    gap: 10,
  },
  sectionTitle: {
    ...type.title,
    fontSize: 18,
    lineHeight: 24,
    color: colors.text,
  },
  p: {
    ...type.body,
    flex: 1,
    fontSize: 15,
    lineHeight: 24,
    color: colors.dim,
  },
  strong: {
    color: colors.text,
  },
  bullet: {
    flexDirection: 'row',
    gap: 10,
  },
  bulletMark: {
    ...type.body,
    fontSize: 15,
    lineHeight: 24,
    color: colors.gold,
  },
  callout: {
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.card,
    padding: 14,
  },
  calloutText: {
    ...type.body,
    fontSize: 14,
    lineHeight: 21,
    color: colors.dim,
  },
  mail: {
    color: colors.gold,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: colors.borderSoft,
    paddingTop: 18,
    gap: 10,
    alignItems: 'center',
  },
  footerNav: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 18,
  },
  footerLink: {
    ...type.label,
    fontSize: 10,
    color: colors.dim,
  },
  footerCurrent: {
    ...type.label,
    fontSize: 10,
    color: colors.faint,
  },
  footerNote: {
    ...type.body,
    fontSize: 12,
    color: colors.faint,
    textAlign: 'center',
  },
});
